/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { User } from '@/models/user.model';
import { authService } from '@/services/authService';
import { Clinic } from '@/models/clinic.model';
import { ClinicContext as ClinicContextResponse, clinicService } from '@/services/clinicService';
import { useAuth } from './AuthContext';

interface ClinicContextType {
  clinic: Clinic | null;
  isAdmin: boolean;
  isPractitioner: boolean;
  isClinicStaff: boolean;
  isGuided: boolean;
  isDirect: boolean;
  isLoading: boolean;
  getClinic: (clinicId: string) => Promise<ClinicContextResponse>;
  setClinic: (clinic: Clinic) => void;
  rolePermissions: string[];
  // Trial fields (Phase 5A)
  isTrial: boolean;
  trialDaysRemaining: number;
  trialExpired: boolean;
  // Feature flags (Phase 0, P0-T1)
  featureFlags: Record<string, boolean>;
}

const ClinicContext = createContext<ClinicContextType | undefined>(undefined);
let activeClinicId: string | null = null;

export const getActiveClinicId = () => activeClinicId;

const setActiveClinicId = (clinicId: string | null) => {
  activeClinicId = clinicId;
};

export const clearActiveClinicId = () => {
  setActiveClinicId(null);
};

export const useClinic = () => {
  const context = useContext(ClinicContext);
  if (context === undefined) {
    throw new Error('useClinic must be used within an ClinicProvider');
  }
  return context;
};

interface ClinicProviderProps {
  children: ReactNode;
}

export const ClinicProvider: React.FC<ClinicProviderProps> = ({ children }) => {
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isPractitioner, setIsPractitioner] = useState<boolean>(false);
  const [isClinicStaff, setIsClinicStaff] = useState<boolean>(false);
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user, isLoading: isAuthLoading } = useAuth();
  const getClinic = async (clinicId: string) => {
    try {
      if (clinicId) {
        setIsLoading(true);
        const response = await clinicService.getClinic(clinicId);
        // API returns { data: { clinic, is_admin, ... }, success, message }
        const currentClinic = (response as any).data ?? response;
        setClinic(currentClinic.clinic);
        setActiveClinicId(currentClinic.clinic?.id ?? clinicId);
        setIsAdmin(currentClinic.is_admin);
        setIsPractitioner(currentClinic.is_practitioner);
        setIsClinicStaff(currentClinic.is_clinic_staff);
        setRolePermissions(currentClinic.role_permissions);
        return currentClinic;
      }
    } catch (error) {
      setClinic(null);
      setActiveClinicId(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Compute trial state from clinic fields
  const trialState = useMemo(() => {
    const isTrial = clinic?.is_trial === true && !clinic?.trial_converted_at;
    let trialDaysRemaining = 0;
    let trialExpired = false;

    if (isTrial && clinic?.trial_ends_at) {
      const endsAt = new Date(clinic.trial_ends_at);
      const now = new Date();
      const diffMs = endsAt.getTime() - now.getTime();
      trialDaysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
      trialExpired = diffMs <= 0;
    }

    return { isTrial, trialDaysRemaining, trialExpired };
  }, [clinic?.is_trial, clinic?.trial_ends_at, clinic?.trial_converted_at]);

  useEffect(() => {
    const initializeClinic = async () => {
      if (!user) {
        setClinic(null);
        setActiveClinicId(null);
        setIsLoading(false);
        return;
      }

      // Check if practitioner has multiple clinics
      if (user.hasMultipleClinics && user.clinics && user.clinics.length > 1) {
        // Check localStorage for previously selected clinic
        const storedClinicId = localStorage.getItem('selectedClinicId');
        if (storedClinicId) {
          // Validate stored clinic is still accessible
          const hasAccess = user.clinics.some(c => c.id === storedClinicId);
          if (hasAccess) {
            try {
              await getClinic(storedClinicId);
              return;
            } catch (error) {
              console.error('Failed to load stored clinic:', error);
              // Clear invalid stored clinic
              localStorage.removeItem('selectedClinicId');
              localStorage.removeItem('selectedClinicTimestamp');
            }
          } else {
            // Clear invalid stored clinic
            localStorage.removeItem('selectedClinicId');
            localStorage.removeItem('selectedClinicTimestamp');
          }
        }
        // If no valid stored clinic, don't set clinic (will show selection screen)
        setIsLoading(false);
        return;
      }

      // For single clinic or non-practitioner users, use clinic from user object
      if (user?.clinic?.id) {
        getClinic(user.clinic.id);
      } else {
        setClinic(null);
        setActiveClinicId(null);
        setIsLoading(false);
      }
    };

    if (!isAuthLoading) {
      initializeClinic();
    }
  }, [user, isAuthLoading]);

  useEffect(() => {
    if (!isAuthLoading && !user?.clinic?.id && !user?.hasMultipleClinics) {
      setIsLoading(false);
      setActiveClinicId(null);
    }
  }, [isAuthLoading, user]);

  const value: ClinicContextType = {
    clinic,
    isAdmin,
    isPractitioner,
    isClinicStaff,
    isGuided: clinic?.clinic_archetype === 'guided',
    isDirect: clinic?.clinic_archetype === 'direct',
    isLoading,
    getClinic,
    setClinic,
    rolePermissions,
    // Trial state
    isTrial: trialState.isTrial,
    trialDaysRemaining: trialState.trialDaysRemaining,
    trialExpired: trialState.trialExpired,
    // Feature flags (default all off)
    featureFlags: clinic?.feature_flags || {},
  };

  return <ClinicContext.Provider value={value}>{children}</ClinicContext.Provider>;
};
